import { ApolloProvider } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { UserContextProvider, IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import { URLContext, IUrl, LocationFields, locationFieldsFrom } from '@reverbdotcom/commons/src/url_context';
import { RoutingResult, RoutingResultContext } from '@reverbdotcom/commons/src/routing_result';
import { buildClient } from './lib/apollo_client';
import { IEppoClient } from '@reverbdotcom/commons/src/eppo/eppoClientContext';
import EppoClientContextProvider from '@reverbdotcom/commons/src/components/eppo/EppoClientContextProvider';
import { ThemeContextProvider } from '@reverbdotcom/commons/src/components/theme_context_provider';
import { Theme } from '@reverbdotcom/cadence/components';
import { tradeInAbandonedSprigEvent } from '@reverbdotcom/commons/src/elog/sprig_events';

export interface IProps {
  user: IUser;
  client: ReturnType<typeof buildClient>;
  url: string;
  routingResult?: RoutingResult;
  referer: string;
  children?: React.ReactNode;
  eppoClient?: IEppoClient;
  theme?: Theme;
}

// Do not add to this component. This structure ensures
// the entire component tree is rendered inside React.StrictMode.
// Add to InternalAppProvider if necessary.
export default function AppProvider(props: IProps) {
  return (
    <React.StrictMode>
      <InternalAppProvider {...props} />
    </React.StrictMode>
  );
}

// AppProvider is mounted once per RRE-rendered region (header, routed page
// content, footer), each in its own independent
// React tree, so URLContext can't rely on react-router's own context: a
// navigation triggered in one tree (ie: the routed content) needs to be
// observable from another (ie: the header). pushState/replaceState don't
// fire a native event, so this patches them once globally to also dispatch one
let historyPatched = false;
const LOCATION_CHANGE_EVENT = 'reverb:locationchange';
const HISTORY_METHOD_ACTIONS = { pushState: 'PUSH', replaceState: 'REPLACE' } as const;

function ensureHistoryPatched() {
  if (historyPatched) {
    return;
  }
  historyPatched = true;

  (['pushState', 'replaceState'] as const).forEach((method) => {
    const original = window.history[method];
    window.history[method] = function patchedHistoryMethod(...args: Parameters<History[typeof method]>) {
      const result = original.apply(this, args);
      window.dispatchEvent(new CustomEvent(LOCATION_CHANGE_EVENT, {
        detail: { action: HISTORY_METHOD_ACTIONS[method] },
      }));
      return result;
    };
  });
}

if (typeof window !== 'undefined' && window.history) {
  ensureHistoryPatched();
}

function InternalAppProvider(props: IProps) {
  const urlFromCore = new URL(props.url);
  const [urlContextObject, setUrlContextObject] = useState<LocationFields>(
    locationFieldsFrom(urlFromCore.pathname, urlFromCore.search, urlFromCore.hash),
  );

  useEffect(() => {
    // popstate and hashchange are real browser navigations, i.e. POPs; the
    // patched-history event carries the true action (PUSH or REPLACE).
    function handleLocationChange(event: Event) {
      const action: IUrl['action'] = (event as CustomEvent).detail?.action || 'POP';
      setUrlContextObject(
        locationFieldsFrom(
          window.location.pathname,
          window.location.search,
          window.location.hash,
          action,
          window.history.state,
        ),
      );
    }

    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('hashchange', handleLocationChange);
    window.addEventListener(LOCATION_CHANGE_EVENT, handleLocationChange);

    if (document.referrer.includes('sell/trade-in') && !document.referrer.includes('completed=true')) {
      tradeInAbandonedSprigEvent();
    }

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('hashchange', handleLocationChange);
      window.removeEventListener(LOCATION_CHANGE_EVENT, handleLocationChange);
    };
  }, []);

  // Components rendered by the router read URLContext from RouterURLProvider
  // (commons/src/url_context.tsx, rendered by RootRoute) instead, which overrides this value.
  return (
    <URLContext.Provider value={{ ...urlContextObject, referer: props.referer }}>
      <RoutingResultContext.Provider value={props.routingResult || {}}>
        <UserContextProvider user={props.user}>
          <EppoClientContextProvider eppoClient={props.eppoClient}>
            <ApolloProvider client={props.client}>
              <ThemeContextProvider theme={props.theme}>
                {props.children}
              </ThemeContextProvider>
            </ApolloProvider>
          </EppoClientContextProvider>
        </UserContextProvider>
      </RoutingResultContext.Provider>
    </URLContext.Provider>
  );
}
